import React from 'react';
import { Mic, MicOff } from 'lucide-react';

const AudioLevelIndicator = ({ audioLevel = 0 }) => {
  const bars = 20;
  const activeBars = Math.floor((audioLevel / 100) * bars);
  return (
    <div className="flex items-center justify-center gap-1 h-12">
      {[...Array(bars)].map((_, i) => (
        <div
          key={i}
          className={`w-1 rounded-full transition-all ${
            i < activeBars
              ? i < bars * 0.6
                ? 'bg-green-500'
                : i < bars * 0.8
                ? 'bg-yellow-500'
                : 'bg-red-500'
              : 'bg-gray-300'
          }`}
          style={{ height: `${(i + 1) * (48 / bars)}px` }}
        />
      ))}
    </div>
  );
};

/**
 * Microphone control, audio-level meter, feedback line, and tips panel for
 * Child Mode practice. Adult Mode is realtime — when isRealtimeMode is true,
 * the mic button is hidden (the realtime hook drives the recorder).
 */
export const AyahControls = ({
  isKidsMode,
  practiceMode,
  isRealtimeMode,
  isRecording,
  startRecording,
  stopRecording,
  audioLevel,
  feedback,
  errorCount,
  totalErrors,
  correctLabel,
}) => (
  <div
    className={`p-8 text-center ${
      isKidsMode
        ? 'bg-white rounded-3xl shadow-[0_8px_0_0_rgba(0,0,0,0.1)]'
        : 'bg-white/70 backdrop-blur-sm rounded-xl border border-stone-200'
    }`}
    data-testid="ayah-controls"
  >
    {practiceMode !== 'listen' && !isRealtimeMode && (
      <div className="flex flex-col items-center gap-2 mb-6">
        <button
          onClick={isRecording ? stopRecording : startRecording}
          className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto transition-all ${
            isRecording
              ? 'bg-red-600 animate-pulse shadow-lg'
              : isKidsMode
              ? 'bg-green-400 hover:scale-110'
              : 'bg-emerald-700 hover:scale-110'
          }`}
          data-testid="record-button"
        >
          {isRecording ? (
            <MicOff className="w-10 h-10 text-white" />
          ) : (
            <Mic className="w-10 h-10 text-white" />
          )}
        </button>
        <div className="text-sm text-center text-gray-600">
          {isRecording ? 'Okuyun ve durdurun' : 'Başlamak için tıklayın'}
        </div>
      </div>
    )}

    {isRecording && !isRealtimeMode && (
      <div className="mb-6">
        <div className="text-xs text-gray-500 mb-2">Ses Seviyesi</div>
        <AudioLevelIndicator audioLevel={audioLevel} />
      </div>
    )}

    {feedback && (
      <div
        className={`text-lg font-bold mb-4 ${
          feedback.includes(correctLabel)
            ? 'text-green-600'
            : feedback.includes('⚠️')
            ? 'text-red-600'
            : 'text-orange-600'
        }`}
        data-testid="feedback-message"
      >
        {feedback}
      </div>
    )}

    <div className="text-sm text-gray-500">
      {isRecording ? 'Okuyun...' : 'Başlamak için mikrofona tıklayın'}
    </div>

    {!isRecording && (
      <div className={`mt-4 p-3 rounded-lg ${isKidsMode ? 'bg-blue-50 rounded-2xl' : 'bg-blue-50'}`}>
        <p className="text-xs text-blue-700">
          💡 <strong>İpucu:</strong> İlk kullanımda tarayıcınız mikrofon izni isteyecek. "İzin Ver" butonuna tıklayın.
        </p>
        <p className="text-xs text-blue-700 mt-2">
          🎵 <strong>Not:</strong> Sistem sesli okuma sırasında siz okumaya başlarsanız otomatik duracaktır.
        </p>
      </div>
    )}

    <div className="mt-6 flex justify-center gap-4 text-sm">
      <span className="text-gray-600">Hatalar: {errorCount}/2</span>
      <span className="text-gray-600">|</span>
      <span className="text-gray-600">Toplam Hata: {totalErrors}</span>
    </div>
  </div>
);

export default AyahControls;
